import { useLocation } from '@gatsbyjs/reach-router';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import type { Curation } from '@components/DrilldownHeader';
import {
  FormikAutocompleteField,
  FormikFhirDateField,
  FormikTextField,
} from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';

import { APPT_SERVICE_TYPE_MAP } from '@constants/option-types/appointment';
import { APPOINTMENT_HISTORY, ASSESSMENTS_OTHER } from '@constants/routes';

import {
  useCreateAppointment,
  useUpdateAppointment,
} from '@hooks/api/appointment';
import useFhirRecordForm from '@hooks/useFhirRecordForm';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout, DrilldownItem } from '@layouts/DrilldownStyle';

import type {
  AppointmentRecord,
  AppointmentValues,
  FormValues,
} from '@models/appointment';
import { getAppointmentTemplate } from '@models/fhir-templates/appointment';
import type { DrilldownProps } from '@models/record-drilldown';

import { isISO1, isISO2 } from '@utils/fhir';
import { formatFacility } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import type { LocationModelInput } from 'generated/types';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  diagnosis: {
    id: 'appointmentsDrilldown.diagnosis',
    defaultMessage: 'Diagnosis',
  },
  followUps: {
    id: 'appointmentsDrilldown.followUps',
    defaultMessage: 'Follow ups',
  },
  appointmentNotes: {
    id: 'appointmentsDrilldown.appointmentNotes',
    defaultMessage: 'Appointment Notes',
  },
  visitDate: {
    id: 'appointmentsDrilldown.visitDate',
    defaultMessage: 'Visit',
  },
  appointment: {
    id: 'appointmentsDrilldown.appointment',
    defaultMessage: 'Appointment',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/ Province',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  visitReason: {
    id: 'appointmentsDrilldown.visitReason',
    defaultMessage: 'Visit Reason',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
  serviceType: {
    id: 'appointmentsDrilldown.serviceType',
    defaultMessage: 'Appointment Service',
  },
  attachments: {
    id: 'attachments.attachments',
    defaultMessage: 'Attachments',
  },
  assessment: {
    id: 'appointmentsDrilldown.assessment',
    defaultMessage: 'Assessment',
  },
});

interface AppointmentProps extends DrilldownProps<AppointmentRecord> {
  /**
   * When true, suppresses the default redirect to the APPOINTMENTS route
   * after successfully creating a new appointment.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const Appointment: React.FC<AppointmentProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const isAssessment = pathname.includes('assessments');
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const [createAppointment, createAppointmentLoading] = useCreateAppointment();
  const [updateAppointment, updateAppointmentLoading] = useUpdateAppointment();

  const loading =
    baseLoading ||
    createAppointmentLoading ||
    updateAppointmentLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    title: data?.visitReason ?? '',
    appointmentNotes: data?.appointmentNotes ?? '',
    diagnosis: data?.diagnosis ?? '',
    facilityCity: data?.facilityCity ?? '',
    facilityCountry: data?.facilityCountry ?? '',
    facilityName: data?.facilityName ?? '',
    facilityProvince: data?.facilityProvince ?? '',
    followUps: data?.followUps ?? '',
    hcpName: data?.hcpName ?? '',
    selfReported: data?.selfReported ?? false,
    visitDate: data?.visitDate ?? '',
    showTime: data?.showTime ?? true,
    serviceType: data?.serviceType ?? '',
    associatedResources: [],
  };

  const apptValidationSchema: Schema<Partial<AppointmentValues>> =
    object().shape({
      title: string().required(formatMessage(messages.required)),
      appointmentNotes: string().nullable(),
      diagnosis: string().nullable(),
      facilityCity: string(),
      facilityCountry: string(),
      facilityName: string(),
      facilityProvince: string(),
      followUps: string().nullable(),
      hcpName: string().nullable(),
      selfReported: boolean().required(formatMessage(messages.required)),
      visitDate: string().required(formatMessage(messages.required)),
      associatedResources: array().of(
        string().required(formatMessage(messages.required))
      ),
      showTime: boolean(),
      serviceType: string().required(formatMessage(messages.required)),
    });
  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = apptValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) {
      throw new Error('No patient id');
    }
    await validateForm(stripEmptyStrings({ ...values }));

    let appointment: AppointmentRecord | null | undefined;

    try {
      const location: LocationModelInput | null =
        values.facilityCity ||
        values.facilityCountry ||
        values.facilityName ||
        values.facilityProvince
          ? {
              name: values.facilityName,
              ...((values.facilityCity ||
                values.facilityCountry ||
                values.facilityProvince) && {
                address: {
                  city: values.facilityCity,
                  country: values.facilityCountry,
                  state: values.facilityProvince,
                },
              }),
            }
          : null;

      if (data?.id) {
        const { data: updatedAppt } = await updateAppointment({
          input: {
            appointment_id: data.id,
            appointment: data.getModelInput(values),
            location,
          },
        });
        appointment = updatedAppt;
      } else {
        const { data } = await createAppointment({
          input: {
            appointment: getAppointmentTemplate(patient_id, values),
            location,
          },
        });
        appointment = data;
      }
    } catch {
      /* empty */
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: appointment?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (appointment && isCreating && !disableNavigateOnCreate) {
        navigate(`${APPOINTMENT_HISTORY}${appointment.id}`);
      }
      onSubmit?.();
    }
  }

  const backRoute = isSharing
    ? undefined
    : isAssessment
      ? ASSESSMENTS_OTHER
      : APPOINTMENT_HISTORY;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            <FormikAutocompleteField
              displayValue={
                data?.serviceType
                  ? APPT_SERVICE_TYPE_MAP[data.serviceType]
                  : undefined
              }
              getOptionLabel={(option: string) =>
                option in APPT_SERVICE_TYPE_MAP
                  ? APPT_SERVICE_TYPE_MAP[option]
                  : option
              }
              isEditing={isModifying ?? false}
              label={formatMessage(messages.serviceType)}
              name="serviceType"
              onChange={(value) => setFieldValue('serviceType', value)}
              options={Object.entries(APPT_SERVICE_TYPE_MAP)
                .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB))
                .map(([k]) => k)}
              required
              value={values.serviceType}
            />

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.diagnosis)}
                name="diagnosis"
                value={data?.diagnosis}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.followUps)}
                name="followUps"
                value={data?.followUps}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                charLimit={100000}
                label={formatMessage(messages.appointmentNotes)}
                name="appointmentNotes"
                type="text-area"
                value={data?.appointmentNotes}
              />
            </ColumnLayout>

            <RelatedRecordsForm associations={values.associatedResources} />

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );

        const drilldownProps: Curation.DrilldownHeaderProps = {
          /*
          actions: [
            {
              title: 'edit',
              handleClick: () => {
                startEditingRecord();
                navigate(window.location.pathname);
              },
            },
            {
              title: 'delete',
              handleClick: () => {
                navigate(window.location.pathname);
              },
            },
          ],
        */
          items: [
            {
              editingComponent: (
                <FormikTextField
                  isHeaderItem
                  name="hcpName"
                  placeholder={formatMessage(messages.practitioner)}
                />
              ),
              title: data?.hcpName,
              iconName: 'user-doctor',
            },
            {
              editingComponent: (
                <>
                  <FormikTextField
                    isHeaderItem
                    name="facilityName"
                    placeholder={formatMessage(messages.facility)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="facilityCity"
                    placeholder={formatMessage(messages.city)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="facilityProvince"
                    placeholder={formatMessage(messages.region)}
                  />
                  <FormikTextField
                    isHeaderItem
                    name="facilityCountry"
                    placeholder={formatMessage(messages.country)}
                  />
                </>
              ),
              title: formatFacility(
                data?.facilityName,
                data?.facilityCity,
                data?.facilityProvince,
                data?.facilityCountry
              ),
              iconName: 'location-dot',
            },
          ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <FormikFhirDateField
              formats={['date', 'datetime', 'year']}
              // Appointments can be scheduled in the future, so we can't cap
              // at today (unlike other records). A bounded max is still
              // required so the native date input's year segment stops at
              // 4 digits instead of accepting an unbounded number of digits.
              max="9999-12-31"
              name="visitDate"
              onChange={(value) => {
                setFieldValue('visitDate', value);

                if (!value || isISO1(value) || isISO2(value)) {
                  setFieldValue('showTime', true);
                } else {
                  setFieldValue('showTime', false);
                }
              }}
              prefix={formatMessage(messages.visitDate)}
              value={data?.visitDate}
            />
          ) : (
            data?.getVisitDateStr(true)
          ),
          title:
            data?.visitReason ||
            formatMessage(
              isAssessment ? messages.assessment : messages.appointment
            ),
          titleInput: (
            <FormikTextField
              name="title"
              placeholder={formatMessage(messages.visitReason)}
            />
          ),
        };

        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                associatedRecords={data?.associatedRecords}
                backRoute={backRoute}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default Appointment;
